// tool-state-store.ts
//
// Đọc/ghi <project>/.claude/avatar-tools.json — state file project-level ghi
// nhận tool nào ĐANG bật (ý định Avatar, tách khỏi settings.json của Claude Code).
//
// Vì sao project-level (không phải ~/.avatar): bật/tắt tool là quyết định CỦA
// DỰ ÁN — cả team nên giống nhau → file commit được. `sync` chạy trong project
// context nên đọc tự nhiên để re-apply.
//
// Schema:
//   {
//     "tools": {
//       "prompt-scoring": { "enabled": true, "version": "0.1.0", "appliedAt": "ISO" }
//     }
//   }
//
// Safety: atomic write (qua filesystem-helpers). Parse lỗi → coi như empty state
// (fail-soft, không crash CLI vì 1 file state hỏng).

import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";

export const TOOL_STATE_RELATIVE_PATH = ".claude/avatar-tools.json";

export interface ToolStateEntry {
  enabled: boolean;
  version: string;
  appliedAt: string; // ISO timestamp lần enable/disable gần nhất
}

export interface ToolState {
  tools: Record<string, ToolStateEntry>;
}

function emptyState(): ToolState {
  return { tools: {} };
}

function stateFilePath(workspacePath: string): string {
  return join(workspacePath, TOOL_STATE_RELATIVE_PATH);
}

// Đọc state. File thiếu hoặc parse lỗi → empty state (fail-soft).
export async function readToolState(workspacePath: string): Promise<ToolState> {
  const path = stateFilePath(workspacePath);
  if (!(await pathExists(path))) {
    return emptyState();
  }
  try {
    const parsed = await readJson<Partial<ToolState>>(path);
    // Defensive: file tồn tại nhưng thiếu `tools` → normalize.
    return { tools: parsed.tools ?? {} };
  } catch {
    return emptyState();
  }
}

// #148: như readToolState nhưng phân biệt corrupt = file TỒN TẠI mà parse fail
// (vd merge conflict marker — file commit được). File THIẾU → state trống hợp lệ
// (corrupt:false). Caller sync dùng cờ này để KHÔNG coi state hỏng là "0 entry"
// rồi auto-enable nhầm cả tool member đã disable.
export async function readToolStateWithHealth(
  workspacePath: string,
): Promise<{ state: ToolState; corrupt: boolean }> {
  const path = stateFilePath(workspacePath);
  if (!(await pathExists(path))) {
    return { state: emptyState(), corrupt: false };
  }
  try {
    const parsed = await readJson<Partial<ToolState>>(path);
    return { state: { tools: parsed.tools ?? {} }, corrupt: false };
  } catch {
    return { state: emptyState(), corrupt: true };
  }
}

// Ghi state atomic. Giữ nguyên các field không quản lý nếu sau này mở rộng.
export async function writeToolState(workspacePath: string, state: ToolState): Promise<void> {
  await writeJsonAtomic(stateFilePath(workspacePath), state);
}

// Set 1 tool enabled/disabled + stamp version & appliedAt. Trả về state mới.
export async function setToolState(
  workspacePath: string,
  name: string,
  entry: { enabled: boolean; version: string },
): Promise<ToolState> {
  const state = await readToolState(workspacePath);
  state.tools[name] = {
    enabled: entry.enabled,
    version: entry.version,
    appliedAt: new Date().toISOString(),
  };
  await writeToolState(workspacePath, state);
  return state;
}

// Tiện ích: danh sách tên tool đang enabled=true (dùng cho sync re-apply).
export async function listEnabledTools(workspacePath: string): Promise<string[]> {
  const state = await readToolState(workspacePath);
  return Object.entries(state.tools)
    .filter(([, entry]) => entry.enabled)
    .map(([name]) => name);
}
